import type { CreateWorkflowData, UpdateWorkflowData, Workflow } from "@/types/workflow";

// Mock Workflows Storage
let mockWorkflows: Workflow[] = [];

// Default Workflow - Pre-configured
const defaultWorkflow: Workflow = {
    workflow_id: "default",
    name: "Workflow Default - Gestione Email e Documenti",
    description:
        "Workflow predefinito per la gestione automatica di email e documenti. Analizza la casella email, classifica documenti, estrae contenuti e valida secondo gli schemi.",
    is_default: true,
    status: "active",
    account_ids: undefined, // Default workflow available for all accounts
    nodes: [
        // Start Node
        {
            id: "start-1",
            type: "start",
            position: { x: 412, y: 218 },
            data: {
                label: "Start",
            },
        },
        // Postino/Mailer Agent
        {
            id: "agent-postino-1",
            type: "agent",
            position: { x: 400, y: 300 },
            data: {
                label: "Postino/Mailer",
                agent_type: "postino",
                config: {
                    email_providers: ["gmail", "outlook"],
                    check_interval: "5min",
                },
            },
        },
        // Decision: Tipo Entità
        {
            id: "decision-entity-1",
            type: "decision",
            position: { x: 404, y: 404 },
            data: {
                label: "Tipo Entità?",
                condition: {
                    field: "entity_type",
                    operator: "equals",
                    value: ["ordine", "richiesta_preventivo", "preventivo", "container"],
                },
            },
        },
        // Classificatore Agent (solo per Container con documenti)
        {
            id: "agent-classificatore-1",
            type: "agent",
            position: { x: 416, y: 560 },
            data: {
                label: "Classificatore",
                agent_type: "classificatore",
                config: {
                    document_types: ["bill_of_lading", "commercial_invoice", "phytosanitary_certificate"],
                },
            },
        },
        // Decision: Document Type trovato?
        {
            id: "decision-doctype-1",
            type: "decision",
            position: { x: 382, y: 674 },
            data: {
                label: "Document Type trovato?",
                condition: {
                    field: "document_type",
                    operator: "exists",
                    value: null,
                },
            },
        },
        // Parser Agent
        {
            id: "agent-parser-1",
            type: "agent",
            position: { x: 602, y: 814 },
            data: {
                label: "Parser",
                agent_type: "parser",
                config: {
                    extract_content: true,
                    find_schema: true,
                },
            },
        },
        // Validatore Agent
        {
            id: "agent-validatore-1",
            type: "agent",
            position: { x: 604, y: 920 },
            data: {
                label: "Validatore",
                agent_type: "validatore",
                config: {
                    validate_attributes: true,
                    check_schema_compliance: true,
                },
            },
        },
        // Decision: Ci sono alert?
        {
            id: "decision-alert-1",
            type: "decision",
            position: { x: 604, y: 1024 },
            data: {
                label: "Ci sono alert?",
                condition: {
                    field: "has_alerts",
                    operator: "equals",
                    value: true,
                },
            },
        },
        // Action: Notifica
        {
            id: "action-notifica-1",
            type: "action",
            position: { x: 688, y: 1155 },
            data: {
                label: "Notifica Errori",
                action_type: "notification_both",
                config: {
                    in_app: true,
                    email: true,
                },
            },
        },
        // End Node
        {
            id: "end-1",
            type: "end",
            position: { x: 378, y: 1268 },
            data: {
                label: "End",
            },
        },
    ],
    edges: [
        {
            id: "e-start-postino",
            source: "start-1",
            target: "agent-postino-1",
            sourceHandle: undefined,
            targetHandle: undefined,
            label: "",
        },
        {
            id: "e-postino-decision-entity",
            source: "agent-postino-1",
            target: "decision-entity-1",
            sourceHandle: undefined,
            targetHandle: undefined,
            label: "",
        },
        {
            id: "e-decision-entity-classificatore",
            source: "decision-entity-1",
            target: "agent-classificatore-1",
            sourceHandle: "yes",
            targetHandle: undefined,
            label: "Container con documenti",
        },
        {
            id: "e-classificatore-decision-doctype",
            source: "agent-classificatore-1",
            target: "decision-doctype-1",
            sourceHandle: undefined,
            targetHandle: undefined,
            label: "",
        },
        {
            id: "e-decision-doctype-parser",
            source: "decision-doctype-1",
            target: "agent-parser-1",
            sourceHandle: "yes",
            targetHandle: undefined,
            label: "Sì",
        },
        {
            id: "e-parser-validatore",
            source: "agent-parser-1",
            target: "agent-validatore-1",
            sourceHandle: undefined,
            targetHandle: undefined,
            label: "",
        },
        {
            id: "e-validatore-decision-alert",
            source: "agent-validatore-1",
            target: "decision-alert-1",
            sourceHandle: undefined,
            targetHandle: undefined,
            label: "",
        },
        {
            id: "e-decision-alert-notifica",
            source: "decision-alert-1",
            target: "action-notifica-1",
            sourceHandle: "yes",
            targetHandle: undefined,
            label: "Sì",
        },
        {
            id: "e-notifica-end",
            source: "action-notifica-1",
            target: "end-1",
            sourceHandle: undefined,
            targetHandle: undefined,
            label: "",
        },
        {
            id: "e-decision-alert-end",
            source: "decision-alert-1",
            target: "end-1",
            sourceHandle: "no",
            targetHandle: undefined,
            label: "No",
        },
        {
            id: "e-decision-doctype-end",
            source: "decision-doctype-1",
            target: "end-1",
            sourceHandle: "no",
            targetHandle: undefined,
            label: "No",
        },
    ],
    created_at: new Date("2024-01-01T00:00:00Z"),
    updated_at: new Date("2024-01-01T00:00:00Z"),
};

// Initialize with default workflow
mockWorkflows = [defaultWorkflow];

// Mock Workflow Service
export const workflowService = {
    // Get all workflows
    getAll(): Workflow[] {
        return [...mockWorkflows];
    },

    // Get workflow by ID
    getById(workflowId: string): Workflow | undefined {
        return mockWorkflows.find((w) => w.workflow_id === workflowId);
    },

    // Create new workflow
    create(data: CreateWorkflowData): Workflow {
        const newWorkflow: Workflow = {
            workflow_id: `workflow-${Date.now()}`,
            name: data.name,
            description: data.description,
            is_default: false,
            status: data.status || "draft",
            nodes: data.nodes || [],
            edges: data.edges || [],
            account_ids: data.account_ids,
            created_at: new Date(),
            updated_at: new Date(),
        };

        mockWorkflows.push(newWorkflow);
        return newWorkflow;
    },

    // Update workflow
    update(workflowId: string, data: UpdateWorkflowData): Workflow | null {
        const index = mockWorkflows.findIndex((w) => w.workflow_id === workflowId);
        if (index === -1) return null;

        // Prevent updating default workflow (except node positions)
        if (mockWorkflows[index].is_default && data.nodes === undefined && data.edges === undefined) {
            throw new Error("Cannot update default workflow");
        }

        const updated: Workflow = {
            ...mockWorkflows[index],
            ...data,
            nodes: data.nodes ?? mockWorkflows[index].nodes,
            edges: data.edges ?? mockWorkflows[index].edges,
            updated_at: new Date(),
        };

        mockWorkflows[index] = updated;
        return updated;
    },

    // Update node positions only (allowed even for default workflow)
    updateNodePositions(workflowId: string, nodes: Workflow["nodes"]): Workflow | null {
        const index = mockWorkflows.findIndex((w) => w.workflow_id === workflowId);
        if (index === -1) return null;

        // Update only positions, keep everything else
        const updatedNodes = mockWorkflows[index].nodes.map((node) => {
            const updatedNode = nodes.find((n) => n.id === node.id);
            if (updatedNode) {
                return {
                    ...node,
                    position: updatedNode.position,
                };
            }
            return node;
        });

        const updated: Workflow = {
            ...mockWorkflows[index],
            nodes: updatedNodes,
            updated_at: new Date(),
        };

        mockWorkflows[index] = updated;
        return updated;
    },

    // Update edges (allowed even for default workflow - only handle positions)
    updateEdges(workflowId: string, edges: Workflow["edges"]): Workflow | null {
        const index = mockWorkflows.findIndex((w) => w.workflow_id === workflowId);
        if (index === -1) return null;

        // Update only edges, keep everything else
        const updated: Workflow = {
            ...mockWorkflows[index],
            edges: edges,
            updated_at: new Date(),
        };

        mockWorkflows[index] = updated;
        return updated;
    },

    // Delete workflow
    delete(workflowId: string): boolean {
        const index = mockWorkflows.findIndex((w) => w.workflow_id === workflowId);
        if (index === -1) return false;

        // Prevent deleting default workflow
        if (mockWorkflows[index].is_default) {
            throw new Error("Cannot delete default workflow");
        }

        mockWorkflows.splice(index, 1);
        return true;
    },

    // Get default workflow
    getDefault(): Workflow | undefined {
        return mockWorkflows.find((w) => w.is_default);
    },
};
